import { getOpportunityPropertiesErrors, opportunityPropertiesSchema } from '@opportunities/-lib/opportunityValidation'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { OpportunityListResponse } from 'backend/types'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useState } from 'react'
import { DATE, OPPORTUNITY_TYPE_IDS, type OpportunityTypeId, VALIDATION_MESSAGES } from 'shared'
import { toast } from 'sonner'
import { type CreateOpportunityData, createOpportunity, getChatMembers } from '@/lib'
import { getVisibleErrors, touchAll } from '@/lib/form-validation'
import { queryKeys } from '@/lib/queryKeys'

/**
 * CopyDialog用のオポチュニティデータ型
 */
interface OpportunityData {
  id: number
  salesforce_project_id: number | null
  title: string
  information_source?: string | null
  description?: string | null
  is_expansion?: boolean
  start_on?: string | null
  contact_name?: string | null
}

/**
 * useCopyDialog フックのパラメータ
 */
interface UseCopyDialogParams {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunity: OpportunityData | null
  opportunityTypeId: OpportunityTypeId
}

/**
 * CopyDialogのビジネスロジックを管理するカスタムフック
 *
 * オポチュニティのコピー/新規作成に必要な状態管理、データ取得、バリデーション、送信処理を提供する
 */
export function useCopyDialog({ open, onOpenChange, opportunity, opportunityTypeId }: UseCopyDialogParams) {
  const queryClient = useQueryClient()

  // 初期フォームデータを生成する関数
  const createInitialFormData = useCallback((): CreateOpportunityData => {
    if (opportunity) {
      // コピーモード: 既存オポチュニティのデータをコピー
      // startOnの日付バリデーション（1年以上前はコピーしない）
      let validStartOn: string | null = null
      if (opportunity.start_on) {
        const startDate = DateTime.fromISO(opportunity.start_on)
        const oneYearAgo = DateTime.local().minus({ years: 1 })
        if (startDate.isValid && startDate >= oneYearAgo) {
          validStartOn = startDate.toFormat('yyyy-MM-dd')
        }
      }

      return {
        salesforceProjectId: opportunity.salesforce_project_id || 0,
        opportunityTypeId,
        title: opportunity.title,
        informationSource: opportunity.information_source ?? null,
        description: opportunity.description ?? null,
        isExpansion: opportunity.is_expansion ?? false,
        startOn: validStartOn,
        contactName: opportunity.contact_name ?? null,
      }
    }

    // 新規作成モード: 空のフォーム
    return {
      salesforceProjectId: 0,
      opportunityTypeId,
      title: '',
      informationSource: null,
      description: null,
      isExpansion: false,
      startOn: null,
      contactName: null,
    }
  }, [opportunity, opportunityTypeId])

  // フォームの状態
  const [formData, setFormData] = useState<CreateOpportunityData>(() => createInitialFormData())

  // 一度でも編集されたかどうかのフラグ
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // ダイアログが閉じたときに状態をリセット
  useEffect(() => {
    if (!open) {
      setFormData(createInitialFormData())
      setTouched({})
    }
  }, [open, createInitialFormData])

  // バリデーション結果（レンダリング時に直接計算）
  const allErrors = (() => {
    const propertyErrors = getOpportunityPropertiesErrors({
      title: formData.title,
      informationSource: formData.informationSource ?? '',
      description: formData.description ?? '',
      contactName: formData.contactName ?? '',
    })

    const newErrors: Record<string, string> = { ...propertyErrors }

    // 案件選択は必須（新規作成モードのみ）
    if (!opportunity && !formData.salesforceProjectId) {
      newErrors.salesforceProjectId = VALIDATION_MESSAGES.REQUIRED_SELECT
    }

    return newErrors
  })()

  // 表示用エラー（touched なフィールドのみ表示）
  const errors = getVisibleErrors(allErrors, touched)

  // エラーがあるかどうか（表示中のエラーベースでチェック）
  const hasErrors = Object.keys(errors).length > 0

  // チャットメンバープレビュー取得
  const { data: chatMembersData, isLoading: isChatMembersLoading } = useQuery({
    queryKey: queryKeys.chatMembers(formData.salesforceProjectId),
    queryFn: async () => {
      if (!formData.salesforceProjectId) throw new Error('No project ID')
      return getChatMembers(formData.salesforceProjectId)
    },
    enabled: formData.salesforceProjectId > 0 && !opportunity, // 新規作成モードでプロジェクト選択時のみ
  })

  // オポチュニティ作成
  const createMutation = useMutation({
    mutationFn: async (data: CreateOpportunityData) => {
      return createOpportunity(data)
    },
    onSuccess: (createdOpportunity) => {
      toast.success('オポチュニティを作成しました')

      // 一覧のキャッシュを楽観的更新（新しいオポチュニティを先頭に追加）
      queryClient.setQueriesData<OpportunityListResponse>(
        { queryKey: queryKeys.opportunities.all() },
        (oldData: OpportunityListResponse | undefined) => {
          if (!oldData) return oldData

          return {
            ...oldData,
            opportunities: [createdOpportunity, ...oldData.opportunities],
            total: oldData.total + 1,
          }
        },
      )

      onOpenChange(false)
    },
    onError: () => {
      toast.error('オポチュニティの作成に失敗しました')
    },
  })

  /**
   * フォームデータ更新（touched フラグも更新）
   */
  const updateFormData = (updates: Partial<CreateOpportunityData>) => {
    setFormData((prev) => ({ ...prev, ...updates }))
    // 更新されたフィールドを touched にする
    const touchedKeys = Object.keys(updates)
    setTouched((prev) => {
      const newTouched = { ...prev }
      for (const key of touchedKeys) {
        newTouched[key] = true
      }
      return newTouched
    })
  }

  /**
   * 詳細テキストエリアの変更ハンドラ
   */
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    updateFormData({ description: newValue || null })
  }

  /**
   * フォーム送信ハンドラ
   *
   * バリデーションを実行し、成功した場合はオポチュニティ作成のミューテーションを実行する
   */
  const handleSubmit = () => {
    // 送信時に全フィールドを touched にする
    setTouched(touchAll(opportunityPropertiesSchema, 'salesforceProjectId'))

    if (Object.keys(allErrors).length > 0) return

    // chatMembersDataからchatMembersとchatCustomerNameを追加
    const requestData: CreateOpportunityData = {
      ...formData,
      chatMembers: chatMembersData?.members.map((m) => ({ memberEmail: m.memberEmail })),
      chatCustomerName: chatMembersData?.members[0]?.customerName,
    }

    createMutation.mutate(requestData)
  }

  /**
   * ダイアログを閉じるハンドラ
   */
  const handleClose = () => {
    onOpenChange(false)
  }

  // ダイアログのタイトルを決定
  const opportunityTypeLabel =
    opportunityTypeId === OPPORTUNITY_TYPE_IDS.CAPTAIN ? 'キャプテンオポチュニティ' : '営業オポチュニティ'
  const dialogTitle = opportunity ? `既存${opportunityTypeLabel}から新規作成` : `${opportunityTypeLabel}登録`

  return {
    // 状態
    formData,
    updateFormData,
    errors,
    hasErrors,
    chatMembersData,
    isChatMembersLoading,
    isPending: createMutation.isPending,
    dialogTitle,
    opportunityTypeId,
    opportunity,
    dateConstraints: {
      min: DATE.oneYearAgo,
      max: DATE.oneYearLater,
    },

    // ハンドラ
    handleDescriptionChange,
    handleSubmit,
    handleClose,
  }
}
